// TimeSlotGrid.jsx - Saat slotlarını göster
import React, { useState, useEffect } from 'react';
import { getAvailableSlots } from '../../utils/newApi';

const TimeSlotGrid = ({ roomId, date, selectedSlot, onSelectSlot, duration = 1 }) => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (roomId && date) {
      fetchSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, date]);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateString = typeof date === 'string' ? date : formatDate(date);
      const availableSlots = await getAvailableSlots(roomId, dateString);
      setSlots(availableSlots);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setError(err.message || 'Saatler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Saat slotunun sonunu hesapla
  const getEndTime = (startTime, duration) => {
    const [hour, minute] = startTime.split(':').map(Number);
    const endHour = hour + duration;
    return `${endHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // Geçmiş saat mi kontrol et
  const isPastTime = (timeString) => {
    const now = new Date();
    const selectedDate = new Date(date);
    
    // Aynı gün değilse geçmiş kontrolü yapma
    const isToday = selectedDate.toDateString() === now.toDateString();
    if (!isToday) return false;
    
    // Slot saati ile şu anki saati karşılaştır
    const [slotHour, slotMinute] = timeString.split(':').map(Number);
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    const slotTimeInMinutes = slotHour * 60 + slotMinute;
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    
    return slotTimeInMinutes <= currentTimeInMinutes;
  };

  // Slot seçilebilir mi kontrol et (süre boyunca tüm slotlar boş olmalı + geçmiş saat değil)
  const isSlotSelectable = (slot, index) => {
    if (!slot.available) return false;
    
    // Geçmiş saat kontrolü
    if (isPastTime(slot.time)) return false;
    
    // Duration boyunca sonraki slotları kontrol et
    for (let i = 1; i < duration; i++) {
      const nextSlot = slots[index + i];
      if (!nextSlot || !nextSlot.available) {
        return false;
      }
      // Sonraki slotlar da geçmiş olmamalı
      if (isPastTime(nextSlot.time)) {
        return false;
      }
    }
    
    return true;
  };

  if (!roomId || !date) {
    return (
      <div className="p-6 text-center text-gray-200">
        Lütfen önce oda ve tarih seçiniz
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-200 mt-4">Uygun saatler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-400 mb-2">❌ {error}</div>
        <button
          onClick={fetchSlots}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-blue-100">
          Saat Seçimi
        </h3>
        <div className="text-sm text-gray-200">
          {duration} saat
        </div>
      </div>

      {/* Saat Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {slots.map((slot, index) => {
          const isSelected = selectedSlot === slot.time;
          const selectable = isSlotSelectable(slot, index);
          const endTime = getEndTime(slot.time, duration);

          return (
            <div
              key={slot.time}
              onClick={() => selectable && onSelectSlot(slot.time)}
              className={`
                relative p-3 rounded-lg text-center transition-all duration-200
                ${isSelected
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50 scale-105'
                  : selectable
                  ? 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:scale-105 cursor-pointer'
                  : 'bg-gray-800/30 text-gray-600 cursor-not-allowed'
                }
              `}
            >
              {/* Başlangıç Saati */}
              <div className="text-lg font-bold">
                {slot.time}
              </div>

              {/* Bitiş Saati (duration > 1 ise) */}
              {duration > 1 && selectable && (
                <div className="text-xs opacity-75 mt-1">
                  - {endTime}
                </div>
              )}

              {/* Durum İkonu */}
              {!slot.available && (
                <div className="absolute top-1 right-1">
                  {slot.reason?.includes('Ders') || slot.reason?.includes('Prova') ? (
                    // Tekrarlayan ders/prova
                    <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L11 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c-.25.78-.19 1.621.166 2.36l.812 1.683a.75.75 0 01-.956 1.05A3.989 3.989 0 014 15a3.989 3.989 0 01-.688-2.08.75.75 0 01-.956-1.05l.812-1.683a2.989 2.989 0 01.166-2.36l.818-2.552a1 1 0 011.896.632l-.818 2.552a1 1 0 00.166 1.05L5 11.323V13a1 1 0 11-2 0v-1.677l-.598-1.193a1 1 0 00.166-1.05l.818-2.552a1 1 0 011.896.632z" />
                    </svg>
                  ) : (
                    // Normal rezerve
                    <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              )}

              {/* Seçili İşareti */}
              {isSelected && (
                <div className="absolute top-1 left-1">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}

              {/* Dolu Sebebi */}
              {!slot.available && slot.reason && (
                <div className="text-xs mt-1 text-red-400 truncate" title={slot.reason}>
                  {slot.reason}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Seçili Slot Bilgisi */}
      {selectedSlot && (
        <div className="p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
          <p className="text-sm text-blue-200">
            <strong>Seçili Saat:</strong> {selectedSlot} - {getEndTime(selectedSlot, duration)}
          </p>
        </div>
      )}

      {/* Açıklama */}
      <div className="grid grid-cols-2 gap-4 text-xs text-gray-300">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-800/50 rounded mr-2"></div>
          <span>Uygun</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-800/30 rounded mr-2"></div>
          <span>Dolu</span>
        </div>
        <div className="flex items-center">
          <svg className="w-3 h-3 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
          </svg>
          <span>Rezerve</span>
        </div>
        <div className="flex items-center">
          <svg className="w-3 h-3 text-yellow-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L11 4.323V3a1 1 0 011-1z" />
          </svg>
          <span>Ders/Prova</span>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotGrid;
